import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Banner, Button, ButtonLink, Card, Dialog, Field, formatMoney, Money, PageHeader, Pill, type LinkComponent,
} from '@elysian/ui';
import type { Contact, DocumentTemplate, LedgerDocument, TaxCode } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { Attachments } from '../shared/Attachments';
import { InvoicePaper } from './InvoicePaper';
import { usePrintExtras } from './printing';
import { salesPaths } from './InvoiceEditor';
import { invoiceState, iso, isAmount, longDate, SEND_STATUS, shortDate } from './shared';
import { RecordReceipt, receiptToast } from '../contacts/RecordReceipt';

const RouterLink = Link as unknown as LinkComponent;

interface Loaded { doc: LedgerDocument; contact: Contact | undefined; template: DocumentTemplate | undefined; codes: TaxCode[] }

export const DEFAULT_TEMPLATE: Pick<DocumentTemplate, 'layout' | 'settings'> = {
  layout: 'classic',
  settings: { title: 'Invoice', brand: { accent: '#0F6E56', font: 'dm_sans', logo_position: 'left' }, fields: {}, blocks: [] },
};

export function InvoiceView() {
  const { id = '' } = useParams();
  const { source, context, today } = useSession();
  const now = iso(today());
  const [version, setVersion] = useState(0);
  const data = useAsync<Loaded>(async () => {
    const doc = await source.document(id);
    const [customers, templates, codes] = await Promise.all([source.contacts('customer'), source.templates(), source.taxCodes(doc.issue_date)]);
    const contact = customers.find((c) => c.id === doc.contact_id);
    // #43: the customer's own layout when they have one (it serves their credit notes too), else the default.
    const template = templates.find((t) => !!contact?.invoice_template_id && t.id === contact.invoice_template_id)
      ?? templates.find((t) => t.kind === (doc.type === 'sales_credit' ? 'sales_credit' : 'sales_invoice') && t.is_default)
      ?? templates.find((t) => t.kind === 'sales_invoice' && t.is_default);
    return { doc, contact, template, codes };
  }, [source, id, version]);

  if (data.state === 'loading') return <PageHeader title="Invoice" subtitle="Loading…" />;
  if (data.state === 'error') {
    return (
      <>
        <PageHeader title="Invoice" />
        <Banner action={<Link to="/invoices">All invoices</Link>}>Couldn’t open this invoice: {data.error.message}</Banner>
      </>
    );
  }
  return <Loaded {...data.data} org={context?.organisation} now={now} reload={() => setVersion((v) => v + 1)} />;
}

type Pending = null | 'send' | 'void' | 'post' | 'allocate' | 'receipt' | 'reissue';

function Loaded({ doc, contact, template, codes, org, now, reload }: Loaded & {
  org: Parameters<typeof InvoicePaper>[0]['org'];
  now: string;
  reload: () => void;
}) {
  const { source, can } = useSession();
  const toast = useToast((s) => s.show);
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [to, setTo] = useState(contact?.email ?? '');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [reason, setReason] = useState('');
  const p = salesPaths(doc.type);
  const credit = doc.type === 'sales_credit';
  const s = credit && (doc.status === 'posted' || doc.status === 'part_paid')
    ? { tone: 'info' as const, label: `${formatMoney(doc.outstanding, doc.currency)} to use` }
    : credit && doc.status === 'paid' ? { tone: 'ok' as const, label: 'Fully used' } : invoiceState(doc, now);
  const draft = doc.status === 'draft';
  const open = doc.status === 'posted' || doc.status === 'part_paid';
  const t = template ?? DEFAULT_TEMPLATE;
  const mayPost = can('documents.write');
  const extras = usePrintExtras(t.settings);
  const navigate = useNavigate();
  const [reissueDate, setReissueDate] = useState(now);
  // Credit and reissue: only an invoice with nothing paid or credited against it (#25, #27).
  const mayReissue = !credit && doc.type === 'sales_invoice' && doc.status === 'posted' && (doc.allocations?.length ?? 0) === 0 && mayPost;

  async function reissue() {
    setBusy(true);
    setError(null);
    try {
      const r = await source.creditAndReissue(doc.id, reissueDate, reason.trim());
      toast(`${doc.doc_no} credited by ${r.credit_note.doc_no}. Correct the new draft, then approve it.`);
      navigate(`/invoices/${r.draft.id}/edit`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  async function act(work: () => Promise<string>) {
    setBusy(true);
    setError(null);
    try {
      toast(await work());
      setPending(null);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function openSend() {
    setError(null);
    setTo(contact?.email ?? '');
    setSubject(`${draft ? 'Invoice' : `Invoice ${doc.doc_no}`} from ${org?.legal_name ?? 'us'}`);
    setMessage(`Hello,\n\nPlease find attached our invoice for ${formatMoney(doc.gross_total, doc.currency)}${doc.due_date ? `, due by ${longDate(doc.due_date)}` : ''}.\n\nKind regards,\n${org?.legal_name ?? ''}`);
    setPending('send');
  }

  const send = () => act(async () => {
    const addresses = to.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
    let number = doc.doc_no;
    if (draft) number = (await source.postDocument(doc.id)).doc_no ?? null;
    await source.sendDocument(doc.id, { to: addresses, subject: subject.replace('Invoice from', `Invoice ${number} from`), body: message });
    return draft ? `${number} posted and queued to ${addresses.join(', ')}` : `Queued to ${addresses.join(', ')}`;
  });

  async function openPdf() {
    try {
      const blob = await source.pdf(doc.id);
      if (!blob) {
        toast('PDFs are made by the server. With demo data you see the on-screen preview instead.');
        return;
      }
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <>
      <div style={{ marginBottom: 6 }}><Link to={p.base} className="el-hint">← {p.list}</Link></div>
      <PageHeader
        title={doc.doc_no ?? (doc.import_doc_no ? `Draft ${p.noun} ${doc.import_doc_no}` : `Draft ${p.noun}`)}
        subtitle={<>
          <Link to={`/customers/${doc.contact_id}`}>{doc.contact_name}</Link> · <Money value={doc.gross_total} currency={doc.currency} />
          {open && <> · <Money value={doc.outstanding} currency={doc.currency} /> {credit ? 'left to use' : 'outstanding'}</>}
        </>}
        actions={
          <>
            {draft && <ButtonLink as={RouterLink} to={`${p.base}/${doc.id}/edit`}>Edit</ButtonLink>}
            {open && !credit && mayPost && doc.currency === 'EUR' && <Button onClick={() => { setError(null); setPending('receipt'); }}>Record a payment</Button>}
            {open && !credit && mayPost && <ButtonLink as={RouterLink} to={`/credit-notes/new?invoice=${doc.id}`}>Raise a credit note</ButtonLink>}
            {mayReissue && <Button onClick={() => { setError(null); setReason(''); setReissueDate(now); setPending('reissue'); }}>Credit and reissue</Button>}
            {open && credit && mayPost && <Button onClick={() => { setError(null); setPending('allocate'); }}>Use against invoices</Button>}
            {draft && mayPost && <Button onClick={() => { setError(null); setPending('post'); }}>Approve</Button>}
            {!draft && <Button onClick={() => void openPdf()}>Download PDF</Button>}
            {!draft && doc.status !== 'void' && doc.status !== 'paid' && mayPost && (
              <Button variant="danger" onClick={() => { setError(null); setReason(''); setPending('void'); }}>Void</Button>
            )}
            {doc.status !== 'void' && (draft ? mayPost : true) && (
              <Button variant="primary" onClick={openSend}>{draft ? 'Approve and send' : doc.emails?.length ? 'Send again' : 'Send by email'}</Button>
            )}
          </>
        }
      />
      <div className="el-row" style={{ marginBottom: 16 }}>
        <Pill tone={s.tone}>{s.label}</Pill>
        {doc.template_version && <span className="el-hint">Invoice design version {doc.template_version}</span>}
      </div>
      {error && !pending && <div className="el-error-box" role="alert" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="el-invoice-layout">
        <InvoicePaper doc={doc} contact={contact} org={org} layout={t.layout} settings={t.settings} codes={codes} bank={extras.bank} logoUrl={extras.logoUrl} />
        <div className="el-stack">
        <Card as="section" padding="18px 20px" aria-labelledby="inv-history">
          <h2 id="inv-history" className="el-h2" style={{ marginBottom: 10 }}>History</h2>
          <ol className="el-timeline">
            <li><span>Raised</span><span className="el-hint">{shortDate(doc.issue_date)}</span></li>
            {(doc.emails ?? []).map((e) => (
              <li key={e.id}>
                <span>{SEND_STATUS[e.status]?.label ?? e.status} to {e.to.join(', ')}{e.error ? ` — ${e.error}` : ''}</span>
                <span className="el-hint">{shortDate(e.created_at.slice(0, 10))}</span>
              </li>
            ))}
            {(doc.allocations ?? []).map((a, i) => (
              <li key={i}><span>Payment <Money value={a.amount} currency={doc.currency} /></span><span className="el-hint">{shortDate(a.date)}</span></li>
            ))}
            {doc.status === 'void' && <li><span>Voided. A reversing journal was posted and the number stays used.</span></li>}
            {draft && <li className="el-muted"><span>Not posted yet. Nothing is in the ledger until you approve it.</span></li>}
          </ol>
        </Card>
        <Card as="section" padding="18px 20px" aria-labelledby="inv-files">
          <h2 id="inv-files" className="el-h2" style={{ marginBottom: 10 }}>Files</h2>
          <Attachments files={doc.attachments ?? []} target={{ document_id: doc.id }} canAdd={mayPost && doc.status !== 'void'}
            label={doc.doc_no ?? `this draft ${p.noun}`} onChanged={reload} />
        </Card>
        </div>
      </div>

      {pending === 'receipt' && (
        <RecordReceipt contactId={doc.contact_id} contactName={doc.contact_name} invoiceId={doc.id} onClose={() => setPending(null)}
          onDone={(r) => { setPending(null); toast(receiptToast(r)); reload(); }} />
      )}
      {pending === 'allocate' && (
        <Allocate credit={doc} onClose={() => setPending(null)}
          onDone={(msg) => { setPending(null); toast(msg); reload(); }} />
      )}

      {pending === 'post' && (
        <Dialog title="Approve this invoice?" onClose={() => setPending(null)}
          actions={<><Button variant="ghost" onClick={() => setPending(null)}>Not yet</Button>
            <Button variant="primary" disabled={busy} onClick={() => void act(async () => `${(await source.postDocument(doc.id)).doc_no} posted to the ledger`)}>Approve and post</Button></>}>
          <p style={{ margin: 0 }}>It gets the next invoice number and posts to the ledger. After that it can’t be edited, only voided or credited.</p>
          {error && <div className="el-error-box" role="alert">{error}</div>}
        </Dialog>
      )}

      {pending === 'send' && (
        <Dialog title={draft ? 'Approve and send' : 'Send by email'} onClose={() => setPending(null)}
          actions={<><Button variant="ghost" onClick={() => setPending(null)}>Cancel</Button>
            <Button variant="primary" disabled={busy || !to.trim()} onClick={() => void send()}>{draft ? 'Approve and send' : 'Send'}</Button></>}>
          <Field id="send-to" label="To" hint="Separate several addresses with commas">
            <input id="send-to" className="el-input" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field id="send-subject" label="Subject">
            <input id="send-subject" className="el-input" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Field>
          <Field id="send-body" label="Message">
            <textarea id="send-body" className="el-input" rows={7} value={message} onChange={(e) => setMessage(e.target.value)} />
          </Field>
          <p className="el-hint" style={{ margin: 0 }}>
            {draft ? 'It will be numbered and posted first. ' : ''}The PDF goes as an attachment, in your current invoice design.
          </p>
          {error && <div className="el-error-box" role="alert">{error}</div>}
        </Dialog>
      )}

      {pending === 'reissue' && (
        <Dialog title={`Credit and reissue ${doc.doc_no}?`} onClose={() => setPending(null)}
          actions={<><Button variant="ghost" onClick={() => setPending(null)}>Keep it</Button>
            <Button variant="primary" disabled={busy || reason.trim().length < 3 || !reissueDate || reissueDate < doc.issue_date}
              onClick={() => void reissue()}>Credit and open the new draft</Button></>}>
          <p style={{ margin: 0 }}>A posted invoice can’t be edited, so this puts it right in two steps:</p>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>A credit note for all of {doc.doc_no} (<Money value={doc.gross_total} currency={doc.currency} />) is posted and matched to it, so {doc.contact_name} no longer owes it.</li>
            <li>A new draft opens with the same lines, reference and notes. Correct it, then approve and send it as usual.</li>
          </ol>
          <Field id="ri-date" label="Date of the credit note and new invoice" hint="Not before the invoice. Usually today.">
            <input id="ri-date" type="date" className="el-input" min={doc.issue_date} value={reissueDate} onChange={(e) => setReissueDate(e.target.value)} />
          </Field>
          <Field id="ri-reason" label="What was wrong" hint="Printed on the credit note and kept in the audit trail">
            <input id="ri-reason" className="el-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Wrong quantity on the callout line" />
          </Field>
          {error && <div className="el-error-box" role="alert">{error}</div>}
        </Dialog>
      )}

      {pending === 'void' && (
        <Dialog title={`Void ${doc.doc_no}?`} onClose={() => setPending(null)}
          actions={<><Button variant="ghost" onClick={() => setPending(null)}>Keep it</Button>
            <Button variant="danger" disabled={busy || reason.trim().length < 3}
              onClick={() => void act(async () => { await source.voidDocument(doc.id, now, reason.trim()); return `${doc.doc_no} voided`; })}>Void invoice</Button></>}>
          <p style={{ margin: 0 }}>
            Nothing posted is deleted. A reversing journal is posted today and {doc.doc_no} stays in the sequence, marked void.
            If the customer has paid part of it, issue a credit note instead.
          </p>
          <Field id="void-reason" label="Reason" hint="Kept in the audit trail">
            <input id="void-reason" className="el-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Raised against the wrong customer" />
          </Field>
          {error && <div className="el-error-box" role="alert">{error}</div>}
        </Dialog>
      )}
    </>
  );
}

/** Set a credit note against the same customer's unpaid invoices, oldest first. */
function Allocate({ credit, onClose, onDone }: { credit: LedgerDocument; onClose: () => void; onDone: (msg: string) => void }) {
  const { source } = useSession();
  const invoices = useAsync(async () => (await source.documents({ type: 'sales_invoice', contactId: credit.contact_id }))
    .filter((d) => (d.status === 'posted' || d.status === 'part_paid') && Number(d.outstanding) > 0)
    .sort((a, b) => a.issue_date.localeCompare(b.issue_date)), [source, credit.contact_id]);
  const [amounts, setAmounts] = useState<Record<string, string> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Suggest: oldest invoice first, until the credit is used up.
  const list = invoices.state === 'ready' ? invoices.data : [];
  if (invoices.state === 'ready' && amounts === null) {
    let left = Number(credit.outstanding);
    const start: Record<string, string> = {};
    for (const d of list) {
      const take = Math.min(left, Number(d.outstanding));
      start[d.id] = take > 0 ? take.toFixed(2) : '';
      left = Math.round((left - take) * 100) / 100;
    }
    setAmounts(start);
  }
  const chosen = Object.entries(amounts ?? {}).filter(([, v]) => v.trim() && isAmount(v) && Number(v) > 0);
  const total = chosen.reduce((t, [, v]) => t + Math.round(Number(v) * 100), 0) / 100;
  const over = total > Number(credit.outstanding) + 1e-9;

  async function go() {
    setBusy(true);
    setError(null);
    try {
      await source.allocateCredit(credit.id, chosen.map(([document_id, amount]) => ({ document_id, amount })));
      onDone(`${formatMoney(total.toFixed(2))} of ${credit.doc_no} used against ${chosen.length} ${chosen.length === 1 ? 'invoice' : 'invoices'}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Dialog title={`Use ${credit.doc_no} against invoices`} onClose={onClose}
      actions={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy || !chosen.length || over} onClick={() => void go()}>Use {formatMoney(total.toFixed(2))}</Button></>}>
      <p style={{ margin: 0 }}>{formatMoney(credit.outstanding)} left on the credit note. Nothing is paid out; the invoices’ balances go down.</p>
      {invoices.state === 'loading' && <div className="el-skeleton" style={{ height: 60 }} />}
      {invoices.state === 'ready' && list.length === 0 && <p className="el-muted" style={{ margin: 0 }}>{credit.contact_name} has no unpaid invoices. The credit stays on their account.</p>}
      {list.map((d) => (
        <Field key={d.id} id={`alloc-${d.id}`} label={`${d.doc_no} · ${shortDate(d.issue_date)} · ${formatMoney(d.outstanding)} unpaid`}>
          <input id={`alloc-${d.id}`} className="el-input el-num" inputMode="decimal" value={amounts?.[d.id] ?? ''}
            onChange={(e) => setAmounts({ ...amounts, [d.id]: e.target.value })} />
        </Field>
      ))}
      {over && <div className="el-error-box" role="alert">That’s more than the credit note has left.</div>}
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}
